<template>
    <header class="sticky top-0 z-50 w-full bg-white border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between items-center h-15">
                <a :href="defPath" class="flex-shrink-0" aria-label="Home">
                    <img :src="logo" :alt="projectName" class="w-[120px] h-[50px] object-contain" />
                </a>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        class="p-2 pr-0 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-1 md:hidden"
                        aria-label="Open menu"
                        @click="isMenuOpen = true"
                    >
                        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Teleport to body: vue-island creates a low z-index stacking context that traps fixed children. -->
        <Teleport to="body">
            <Transition name="fade">
                <div
                    v-show="isMenuOpen"
                    class="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm md:hidden"
                    aria-hidden="true"
                    @click="isMenuOpen = false"
                />
            </Transition>

            <Transition name="slide-right">
                <aside
                    v-show="isMenuOpen"
                    class="fixed top-0 right-0 z-[9999] h-full w-[min(320px,88vw)] bg-white shadow-2xl flex flex-col md:hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                <div class="flex items-center justify-between h-20 px-5 border-b border-gray-100">
                    <a :href="defPath" class="flex-shrink-0" @click="isMenuOpen = false" aria-label="Home">
                        <img :src="logo" :alt="projectName" class="h-8 w-auto max-w-[160px] object-contain" />
                    </a>
                    <button
                        type="button"
                        class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        aria-label="Close menu"
                        @click="isMenuOpen = false"
                    >
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <nav class="flex-1 overflow-y-auto py-6 px-4">
                    <ul class="list-none space-y-1 pl-0">
                        <li v-for="(item, index) in menuItems" :key="index">
                            <template v-if="item.children?.length">
                                <span class="block px-4 pb-1 rounded-xl text-xs font-semibold cursor-default"
                                      :class="itemChildrenActive(item) ? 'text-brand-blue' : 'text-gray-700'">
                                    {{ item.translated?.title || '' }}
                                </span>
                                <ul class="list-none ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                    <li v-for="(child, childIndex) in item.children" :key="childIndex">
                                        <a
                                            :href="menuHrefFor(child.path)"
                                            :target="isHelpCenterItem(child.path) ? '_blank' : null"
                                            :rel="isHelpCenterItem(child.path) ? 'noopener noreferrer nofollow' : null"
                                            class="block py-2.5 pr-4 rounded-lg text-xs font-medium transition-colors"
                                            :class="isItemActive(child.path) ? 'bg-brand-blue/10 text-brand-blue font-semibold' : 'text-gray-600 hover:bg-brand-blue/10 hover:text-brand-blue'"
                                            @click="isMenuOpen = false"
                                        >
                                            {{ child.translated?.title || '' }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                            <a
                                v-else
                                :href="menuHrefFor(item.path)"
                                :target="isHelpCenterItem(item.path) ? '_blank' : null"
                                :rel="isHelpCenterItem(item.path) ? 'noopener noreferrer nofollow' : null"
                                class="block px-4 py-1 rounded-xl text-xs font-semibold transition-colors"
                                :class="isItemActive(item.path) ? 'bg-brand-blue/10 text-brand-blue' : 'text-gray-700 hover:bg-brand-blue/10 hover:text-brand-blue'"
                                @click="isMenuOpen = false"
                            >
                                {{ item.translated?.title || '' }}
                            </a>
                        </li>
                    </ul>
                    <div class="mt-8 pt-6 border-t border-gray-100">
                        <p class="px-4 mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">{{ languageLabel }}</p>
                        <div class="relative">
                            <button
                                type="button"
                                class="w-full inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:ring-offset-1"
                                @click="langOpen = !langOpen"
                                :aria-expanded="langOpen"
                            >
                                <img v-if="currentLang?.flag" :src="resolvedFlagSrc(currentLang.flag, currentLang.code)" :alt="currentLang.code_view" class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0" />
                                <span class="flex-1 text-left">{{ currentLang?.code_view || currentLangCode?.toUpperCase() }}</span>
                                <svg class="w-4 h-4 text-gray-500 flex-shrink-0 transition-transform" :class="{ 'rotate-180': langOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                </svg>
                            </button>
                            <Transition name="dropdown">
                                <div v-show="langOpen" class="absolute left-0 right-0 top-full z-10 mt-2 py-2 bg-white rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden">
                                    <div class="grid grid-cols-2 gap-x-1 gap-y-0.5 px-2">
                                        <a
                                            v-for="lang in availableLanguages"
                                            :key="lang.pathCode || lang.code"
                                            :href="lang.path || pathsByLang[lang.pathCode || lang.code] || '#'"
                                            class="flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 transition-colors hover:bg-gray-50 rounded-lg"
                                            :class="{ 'bg-brand-blue/5 text-brand-blue font-semibold': isActiveLang(lang) }"
                                            @click="isMenuOpen = false"
                                        >
                                            <img v-if="lang.flag" :src="resolvedFlagSrc(lang.flag, lang.code)" :alt="lang.translated?.name || lang.code_view || lang.code?.toUpperCase()" class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0" />
                                            <span class="truncate">{{ lang.code_view || lang.code?.toUpperCase() }}</span>
                                        </a>
                                    </div>
                                </div>
                            </Transition>
                        </div>
                    </div>

                    <div v-if="!hideCurrency" class="mt-6 pt-4 border-t border-gray-100">
                        <p class="px-4 mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
                            {{ currencyLabel }}
                        </p>

                        <div class="">
                            <select-app-currency></select-app-currency>
                        </div>
                    </div>

                    <a
                        v-if="ctaText"
                        :href="ctaResolvedHref"
                        class="mt-6 block w-full text-center bg-buy-vignette hover:bg-buy-vignette-hover text-white py-3.5 px-4 rounded-xl font-bold text-base shadow-lg transition-all"
                        @click="isMenuOpen = false"
                    >
                        {{ ctaText }}
                    </a>
                </nav>
                </aside>
            </Transition>
        </Teleport>
    </header>
</template>

<script>
import SelectAppCurrency from './inputs/SelectAppCurrency.vue';
import { isHelpCenterHref, resolveMenuHref } from '../shared/helpCenterResolver';
import { normalizeFlagEntity, resolveFlagUrl } from '../utils/flagUrl.js';

export default {
    name: 'MobileHeader',
    components: {
        SelectAppCurrency
    },
    props: {
        menuItems: { type: Array, default: () => [] },
        logo: { type: String, default: '' },
        projectName: { type: String, default: 'Logo' },
        pathsByLang: { type: Object, default: () => ({}) },
        languages: { type: Array, default: () => [] },
        ctaText: { type: String, default: '' },
        ctaHref: { type: String, default: '' },
        defPath: { type: String, default: '/' },
        currentLangCode: { type: String, default: '' },
        currentPath: { type: String, default: '' },
        /** CMS-driven labels for the slide-out menu sections (mobile header). */
        languageLabel: { type: String, default: 'Language' },
        currencyLabel: { type: String, default: 'Currency' },
        hideCurrency: { type: Boolean, default: false }
    },
    data() {
        return {
            isMenuOpen: false,
            langOpen: false
        };
    },
    computed: {
        languagesByCode() {
            const byCode = {};
            const byUpper = {};
            for (const rawLang of this.languages) {
                const lang = normalizeFlagEntity(rawLang);
                if (!lang || lang.code == null || lang.code === '') continue;
                byCode[lang.code] = lang;
                byUpper[String(lang.code).toUpperCase()] = lang;
            }
            return { byCode, byUpper };
        },
        currentLang() {
            return this.findLanguage(this.currentLangCode);
        },
        /** Լեզուներ, որոնք ցուցադրել (նույն API-ն ինչ desktop: միայն paths_by_language-ում առկա) */
        availableLanguages() {
            const paths = this.pathsByLang || {};
            const { byCode, byUpper } = this.languagesByCode;

            return Object.entries(paths)
                .map(([code, path]) => {
                    const lang = byCode[code] ?? byUpper[String(code).toUpperCase()];
                    if (!lang) return null;
                    return { ...lang, pathCode: code, path };
                })
                .filter(Boolean)
                .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        },
        /** Mobile CTA URL: Blade `ctaHref`, then meta, then /buy-vignette/. */
        ctaResolvedHref() {
            const fromProp = (this.ctaHref || '').trim();
            if (fromProp) {
                return fromProp;
            }
            if (typeof document !== 'undefined') {
                const el = document.querySelector('meta[name="Sloveniavignette-buy-vignette-url"]');
                const raw = el?.getAttribute('content')?.trim();
                if (raw) {
                    try {
                        if (/^https?:\/\//i.test(raw)) {
                            return raw;
                        }
                        const path = raw.startsWith('/') ? raw : `/${raw}`;
                        return new URL(path, window.location.origin).href;
                    } catch {
                        /* fall through */
                    }
                }
            }
            try {
                return new URL('/buy-vignette/', window.location.origin).href;
            } catch {
                return '/buy-vignette/';
            }
        }
    },
    watch: {
        isMenuOpen(open) {
            document.body.style.overflow = open ? 'hidden' : '';
            if (!open) this.langOpen = false;
        }
    },
    methods: {
        findLanguage(code) {
            if (!code) return null;
            const { byCode, byUpper } = this.languagesByCode;
            return byCode[code] ?? byUpper[String(code).toUpperCase()] ?? null;
        },
        isActiveLang(lang) {
            const current = String(this.currentLangCode || '').toUpperCase();
            const candidate = String(lang?.pathCode || lang?.code || '').toUpperCase();
            return current !== '' && current === candidate;
        },
        pathFromHref(href) {
            if (!href || href === '#') return '';
            try {
                const path = typeof href === 'string' ? href : (href.path || '');
                const p = path.replace(/^https?:\/\/[^/]+/, '').replace(/#.*$/, '').trim().replace(/^\/+|\/+$/g, '');
                return p || '';
            } catch (_) { return ''; }
        },
        isItemActive(itemPath) {
            const current = (this.currentPath || '').replace(/^\/+|\/+$/g, '');
            const item = this.pathFromHref(itemPath);
            return item && (current === item || current.startsWith(item + '/'));
        },
        itemChildrenActive(item) {
            return (item.children || []).some(c => this.isItemActive(c.path));
        },
        menuHrefFor(itemPath) {
            return resolveMenuHref(itemPath, this.currentLang || this.currentLangCode);
        },
        isHelpCenterItem(itemPath) {
            return isHelpCenterHref(itemPath);
        },
        resolvedFlagSrc(flag, code = '') {
            return resolveFlagUrl(flag, code);
        }
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
}

.dropdown-enter-active,
.dropdown-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
